import Follow from '@/components/bookmarks/Follow';
import React from 'react';
import { connect } from 'react-redux';
import { selectMatchAction, showMatchInbox } from '../../../app/redux/actions/userMatchActions';
import { AppDispatch } from '../../../app/redux/configureStore';
import { BookmarkInfo } from '../../../app/redux/features/bookmark/bookmarkSlice';
import { UserMatch, UserMatchAction } from '../../../app/redux/reducers/userMatchesReducer';
import { formatCountdown } from '../../../app/utils/date';
import { ContentType, Opportunity } from '../../../types/content';
import Save from '../../bookmarks/Save';
import { MessageOrgLogo } from './MatchBody';
import { useUserMatches } from './hooks/UseUserMatches';
import { getContentClass } from './utils/getContentClass';

interface MatchesInboxProps extends React.HTMLAttributes<HTMLDivElement> {
  updateCurrentItemById: (itemId: string) => void;
  handleTransition: (action: UserMatchAction, match?: UserMatch) => void;
  selectMatch: AppDispatch;
  showMatchInbox: AppDispatch;
}

const MatchesInbox = ({ selectMatch, showMatchInbox, handleTransition, updateCurrentItemById }: MatchesInboxProps) => {
  const { matches, showMatchSelection } = useUserMatches();
  const handleMatchSelection = (matchId: string, v: any) => {
    selectMatch(matchId, v.target.checked);
  };

  return matches?.length > 0 ? (
    <ul className="overflow-x-auto pretty-scroll h-[calc(100%_-_60px)] [&:has(+_#match-footer)]:h-[calc(100%_-_120px)]">
      {matches.map((match: UserMatch, i: number) => {
        const createdAtDate = match?.createdAt ? formatCountdown(match?.createdAt) : null;
        const contentType =
          match?.contentType === 'opportunity' ? (match.content as Opportunity).opportunityType : match?.contentType;
        const messageSeen = match.seen;

        return (
          <li
            key={i}
            className={`py-4 px-5 hover:bg-gray-50 transition duration-200 ease-in-out  cursor-pointer ${
              i + 1 == matches.length ? '' : 'border-b-[1px] border-gray-200'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={match.selected}
                name={`message-${match?.contentId}`}
                onChange={(v) => handleMatchSelection(match?.userMatchId, v)}
                className={
                  showMatchSelection
                    ? 'mr-3 w-4 h-4 text-primary accent-primary border-primary rounded focus:ring-primary ring-offset-2 dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600'
                    : 'hidden'
                }
              />
              <div
                onClick={() => {
                  updateCurrentItemById(match?.userMatchId);
                  showMatchInbox(false);
                }}
                className="flex flex-row items-center w-full"
              >
                <div className="justify-center p-1 rounded-md shadow w-[58px] h-[58px] mr-5 flex-shrink-0">
                  <MessageOrgLogo org={match?.content} />
                </div>
                <div className="w-full text-left flex">
                  <div className="flex-1 items-start">
                    <div className="mb-2 text-sm overflow-hidden break-words">
                      {match?.content?.title && (
                        <div
                          className={`max-w-[15rem] leading-5 line-clamp-2 ${
                            !messageSeen ? 'font-bold text-gray' : 'font-medium text-gray-1000'
                          }`}
                        >
                          {match?.content?.title}
                        </div>
                      )}
                    </div>
                    <div className="flex mb-3">
                      <div className="flex items-center">
                        <div className={`rounded-full h-3 w-3 font-semibold ${getContentClass(match?.contentType)}`} />
                      </div>
                      <div className="uppercase text-gray-700 text-xs font-semibold tracking-wide ml-1">
                        {contentType.replaceAll('_', ' ')}
                      </div>
                    </div>
                    {match?.message && (
                      <p className={`line-clamp-2 text-xs ${!messageSeen ? 'font-medium text-gray' : 'text-gray-700'}`}>
                        {match?.message}
                      </p>
                    )}
                  </div>
                  <div className="pl-4 flex flex-col justify-between items-end pb-1 flex-shrink-0">
                    {createdAtDate && (
                      <>
                        <span
                          className={`text-sm line-0 ${
                            !messageSeen
                              ? 'font-semibold text-gray-700 opacity-100 transition-opacity duration-300'
                              : 'text-gray-700 opacity-50 transition-opacity duration-300'
                          }`}
                        >
                          {createdAtDate}
                        </span>
                        {match.contentType === ContentType.Organisation ||
                        match.contentType === ContentType.CourseProvider ? (
                          <Follow
                            id={`${match.contentId}`}
                            icon
                            bookmarkBtnId={`match_${match.contentId}`}
                            showToast={false}
                            contentType={match.contentType as any}
                            callback={(bookmarkInfo: BookmarkInfo) => {
                              if (match.contentId in bookmarkInfo.bookmarks) {
                                handleTransition(UserMatchAction.Saved, match);
                              }
                            }}
                          />
                        ) : (
                          <Save
                            id={`${match.contentId}`}
                            icon
                            bookmarkBtnId={`match_${match.contentId}`}
                            showToast={false}
                            contentType={match.contentType as any}
                            callback={(bookmarkInfo: BookmarkInfo) => {
                              if (match.contentId in bookmarkInfo.bookmarks) {
                                handleTransition(UserMatchAction.Saved, match);
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  ) : null;
};

const mapActionsToProps = {
  selectMatch: selectMatchAction,
  showMatchInbox: showMatchInbox
};

export default connect(null, mapActionsToProps)(MatchesInbox);
